import type { BirthInput, AnalysisOptions, SajuAnalysis } from './types/input';
import type { PillarPosition } from './types/pillars';
import type { HeavenlyStem } from './types/stems';
import { calculateFourPillars } from './pillars/four-pillars';
import { analyzeTenGods } from './analysis/ten-gods';
import { analyzeTwelveStages } from './analysis/twelve-stages';
import { calculateOhaengDistribution, calculateOhaengDistributionSimple } from './analysis/five-element-dist';
import { analyzeDayMasterStrength } from './analysis/day-master-strength';
import { determineAllYongSin } from './analysis/yong-sin';
import { analyzeGyeokGuk } from './analysis/gyeok-guk';
import { adjustStrengthByGyeokGuk } from './analysis/strength-adjust';
import { analyzeTongbyeon } from './analysis/tongbyeon';
import { analyzeRelations } from './relations/index';
import { analyzeSinsal } from './sinsal/index';
import { getFortuneDirection, calculateFortuneStartAge, calculateMajorFortune } from './fortune/major-fortune';
import { getYearlyFortuneRange } from './fortune/yearly-fortune';
import { getMonthlyFortune } from './fortune/monthly-fortune';
import { getDailyFortuneRange } from './fortune/daily-fortune';
import { getSolarTermsForYear } from './astronomy/solar-terms';
import { getHiddenStems } from './core/hidden-stems';
import { lunarToSolar } from './calendar/lunar-calendar';
import { mergeWithPreset } from './presets';

/**
 * 사주팔자 분석 메인 함수.
 *
 * @param input - 생년월일시 입력 정보
 * @param options - 분석 옵션 (mode 프리셋 지원)
 * @returns 사주 분석 결과
 */
export function analyzeSaju(input: BirthInput, options?: AnalysisOptions): SajuAnalysis {
  // 프리셋 적용: mode에 따라 기본값 merge, 명시 옵션이 우선
  const opts = mergeWithPreset(options);
  const noHour = opts.noHour ?? false;
  // 시간 미상이면 시간 보정 무의미 → 강제 비활성화
  const useTrueSolarTime = noHour ? false : (opts.useTrueSolarTime ?? true);
  const useFixedTimeOffset = noHour ? false : (opts.useFixedTimeOffset ?? false);
  const useYajasi = opts.useYajasi ?? true;

  // 1. Calculate four pillars (includes time correction info)
  const { timeCorrection, ...fourPillars } = calculateFourPillars({
    ...input,
    useTrueSolarTime,
    useFixedTimeOffset,
    useYajasi,
  });

  const dayMaster = fourPillars.day.stem;

  // 2. Ten gods analysis
  const tenGods = analyzeTenGods(fourPillars, noHour);

  // 3. Twelve stages
  const twelveStages = analyzeTwelveStages(fourPillars, noHour);

  // 4. Hidden stems
  const positions: readonly PillarPosition[] = noHour ? ['year', 'month', 'day'] : ['year', 'month', 'day', 'hour'];
  const hiddenStems = Object.fromEntries(
    positions.map(pos => [pos, getHiddenStems(fourPillars[pos].branch)])
  ) as Record<PillarPosition, HeavenlyStem[]>;

  // 5. Five element distribution
  const ohaengDistribution = calculateOhaengDistribution(fourPillars, noHour);
  const ohaengDistributionSimple = calculateOhaengDistributionSimple(fourPillars, noHour);

  // 6. Relations (합충형파해) — strength 계산 전에 실행 (충/합이 통근에 영향)
  const relations = analyzeRelations(fourPillars, noHour, opts.relationOptions);

  // 7. Day master strength (relations를 참조하여 충/합 통근 약화 반영)
  let dayMasterStrength = analyzeDayMasterStrength(fourPillars, noHour, relations, opts.relationOptions);

  // 8. Gyeok-guk (격국) — strength보다 먼저 판정 (geuk-first 모드용)
  const gyeokGuk = analyzeGyeokGuk(fourPillars, dayMasterStrength, noHour, opts);

  // 8-1. geuk-first 모드: 격국 결과로 신강 레벨 보정
  if (opts.strengthMode === 'geuk-first') {
    dayMasterStrength = adjustStrengthByGyeokGuk(dayMasterStrength, gyeokGuk);
  }

  // 9. Yong sin (용신 우선순위 모드 적용) — 보정된 strength 사용
  const yongSinMethods = determineAllYongSin(fourPillars, dayMasterStrength, ohaengDistribution, opts.yongshinPriority);

  // 10. Tongbyeon (통변)
  const tongbyeon = analyzeTongbyeon(tenGods, gyeokGuk, noHour, yongSinMethods);

  // 11. Sinsal (신살 강조 수준 전달)
  const sinsal = analyzeSinsal(fourPillars, noHour, opts.sinsalEmphasis, input.gender);

  // 11. Major fortune (대운)
  const direction = getFortuneDirection(fourPillars.year.stem, input.gender);

  // Resolve the solar calendar date for fortune calculation
  let solarYear = input.year;
  let solarMonth = input.month;
  let solarDay = input.day;
  if (input.calendar === 'lunar') {
    const solar = lunarToSolar(input.year, input.month, input.day, input.isLeapMonth ?? false);
    solarYear = solar.year;
    solarMonth = solar.month;
    solarDay = solar.day;
  }

  const startAge = calculateFortuneStartAge(
    solarYear, solarMonth, solarDay,
    input.hour, input.minute,
    direction,
  );

  // 운세에 격국/용신 활용 여부
  const fortuneYongSin = opts.useYongSinInFortune && yongSinMethods.length > 0 ? yongSinMethods[0] : undefined;
  const fortuneGyeokGuk = opts.useGyeokGukInFortune ? gyeokGuk : undefined;

  const majorFortune = calculateMajorFortune(
    fourPillars.month,
    dayMaster,
    direction,
    startAge,
    solarYear,
    10,
    fourPillars,
    noHour,
    fortuneYongSin,
    fortuneGyeokGuk,
  );

  // 12. Solar terms of the birth year
  const solarTermsOfYear = getSolarTermsForYear(solarYear);

  // 13. Optional: yearly fortune
  let yearlyFortune;
  if (opts.yearlyFortuneCount) {
    const currentYear = new Date().getFullYear();
    yearlyFortune = getYearlyFortuneRange(dayMaster, currentYear, opts.yearlyFortuneCount, fourPillars, noHour, fortuneYongSin, fortuneGyeokGuk);
  }

  // 14. Optional: monthly fortune
  let monthlyFortune;
  if (opts.monthlyFortuneYear) {
    monthlyFortune = getMonthlyFortune(dayMaster, opts.monthlyFortuneYear, fourPillars, noHour, fortuneYongSin, fortuneGyeokGuk);
  }

  // 15. Optional: daily fortune
  let dailyFortune;
  if (opts.dailyFortuneDate) {
    const df = opts.dailyFortuneDate;
    dailyFortune = getDailyFortuneRange(dayMaster, df.year, df.month, df.day, df.count ?? 30, fourPillars, noHour, fortuneYongSin, fortuneGyeokGuk);
  }

  return {
    input,
    fourPillars,
    dayMaster,
    tenGods,
    twelveStages,
    hiddenStems,
    ohaengDistribution,
    ohaengDistributionSimple,
    dayMasterStrength,
    yongSinMethods,
    gyeokGuk,
    tongbyeon,
    relations,
    sinsal,
    majorFortune,
    yearlyFortune,
    monthlyFortune,
    dailyFortune,
    solarTermsOfYear,
    timeCorrection,
  };
}
